import axios from 'axios';
import axiosInstance from './axios'; // Using the project's axios instance for authenticated calls

/**
 * Service to handle communication with Google Gemini AI API.
 */
const aiService = {
    /**
     * Tool Definitions for Gemini Function Calling
     */
    tools: [
        {
            function_declarations: [
                {
                    name: "get_outlets",
                    description: "Retrieve a list of all retail outlets and shops. Use this to find outlet names, addresses, and IDs. Call this if the user asks for outlets or if you need to find an outlet ID for another tool."
                },
                {
                    name: "get_out_of_stock_products",
                    description: "Get a list of products that are currently out of stock or exhausted for a specific outlet.",
                    parameters: {
                        type: "object",
                        properties: {
                            outletId: {
                                type: "string",
                                description: "The ID of the outlet (fetch this using get_outlets if you don't have it)."
                            }
                        },
                        required: ["outletId"]
                    }
                },
                {
                    name: "get_outlet_users",
                    description: "Get a list of users, staff, and their roles for a specific outlet.",
                    parameters: {
                        type: "object",
                        properties: {
                            outletId: {
                                type: "string",
                                description: "The ID of the outlet."
                            }
                        },
                        required: ["outletId"]
                    }
                },
                {
                    name: "get_product_stats",
                    description: "Get sales analytics, top-selling products, and non-selling products for an outlet.",
                    parameters: {
                        type: "object",
                        properties: {
                            outletId: {
                                type: "string",
                                description: "The ID of the outlet."
                            },
                            timeframe: {
                                type: "string",
                                enum: ["weekly", "monthly"],
                                description: "The timeframe for the report (default is weekly)."
                            }
                        },
                        required: ["outletId"]
                    }
                }
            ]
        }
    ],

    /**
     * Executes the actual backend API calls triggered by AI tool requests.
     */
    executeTool: async (name, args) => {
        console.log(`Executing AI Tool: ${name}`, args);
        try {
            switch (name) {
                case "get_outlets": {
                    const res = await axiosInstance.get('outlet');
                    return res.data?.data?.outlets || [];
                }
                case "get_out_of_stock_products": {
                    const res = await axiosInstance.get(`product/getExhaustedProducts?outlet=${args.outletId}`);
                    return res.data?.data?.exhaustedProducts || [];
                }
                case "get_outlet_users": {
                    const res = await axiosInstance.get(`outlet/user?outlet=${args.outletId}`);
                    return res.data?.data?.outletUsers || [];
                }
                case "get_product_stats": {
                    const timeframe = args.timeframe || "weekly";
                    const isWeekly = timeframe === "weekly";
                    const isMonthly = timeframe === "monthly";
                    const url = `dashboard/getProductsStatThroughSummary?outletId=${args.outletId}&weekly=${isWeekly}&monthly=${isMonthly}`;
                    const res = await axiosInstance.get(url);
                    return res.data?.data?.data || {};
                }
                default:
                    return { error: "Tool not found" };
            }
        } catch (err) {
            console.error(`Error executing tool ${name}:`, err);
            return { error: err.message };
        }
    },

    /**
     * Sends a message to Gemini and handles multiple rounds of tool calls if needed.
     */
    sendMessage: async (prompt, context = "") => {
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

        if (!apiKey || apiKey === 'YOUR_GEMINI_API_KEY_HERE') {
            return "I'm not fully connected yet. Please add your Gemini API Key to enable real data fetching!";
        }

        const systemPrompt = `You are the Xpanse Admin Assistant. 
        You have direct access to the live Xpanse database via tools. 
        
        CRITICAL RULES:
        1. If a user asks for data (outlets, users, stock, sales), ALWAYS call the relevant tool immediately. 
        2. NEVER ask the user for an Outlet ID if you can find it yourself. If the user mentions an outlet by name, call 'get_outlets' first, find the ID in the results, and then call the specific data tool.
        3. ALWAYS provide a summary of the data you find. Don't just say you found it.
        4. If the user asks for a table or if you are listing more than 3 items with details, ALWAYS use Markdown tables with headers.
        5. Be helpful, professional, and proactive.
        6. NAVIGATION LINKS: If the user asks where to find a report, page, setting, or screen in the admin panel, or if you mention/suggest a report/page, ALWAYS provide a relative Markdown link to it (e.g., [Open Customer Reports](/reports/customer-reports)). The UI will render this as a clickable button to navigate the user immediately.
        7. CREATOR INFO:
            - If the user asks:
            "Who developed you?"
            "Who created you?"
            "Who made Xpanse AI?"
            "Who built this bot?"
            "Who is your developer?"
            
            Then ALWAYS reply with:

            "Xpanse AI Assistant was developed by Varun Jangale."

            Keep it short and professional.
        
        ADMIN PANEL ROUTE MAP (Use these exact paths for relative links):
        - Dashboard / Home: /dashboard
        - Profile: /view
        - Retail Outlets / Shop List: /outlet-management/outlets
        - Add Outlet: /outlet-management/add-outlet
        - Petty Cash: /outlet-management/petty-cash
        - Outlet Users / Staff: /outlet-management/outlet-users
        - Kiosk Users: /outlet-management/kiosk-outlet-users
        - Bulk Price Update: /automation/bulk-price-update
        - Product Creation: /menu/product-creation
        - Add Menu Item: /menu/add-menu
        - Menu Catalog: /menu/menu-catalog
        - Menu Categories: /menu/category
        - Menu Ingredients: /menu/menu-ingredients
        - Add Menu Ingredients: /menu/add-menu-ingredients
        - Menu Addons: /menu/menu-addons
        - Product Addon Allocation: /menu/product-addon-allocation
        - Beverage Request: /request-intend/beverage-request
        - Central Request: /request-intend/central-request
        - Food Request: /request-intend/food-request
        - All Reports List: /reports/all-reports
        - Customer Registration Reports: /reports/customer-reports
        - Marketing Reports: /reports/marketing-reports
        - Order Reports: /reports/order-reports
        - Kiosk User Reports: /reports/kiosk-user-reports
        - User Analytics Reports: /reports/user-analytics-reports
        - Coupons Report: /reports/coupons-report
        - User Coupons Report: /reports/user-coupon-report
        - Journey Coupon Report: /reports/journey-coupon-report
        - Journey Revenue Report: /reports/journey-revenue-report
        - Day Summary Report: /reports/day-summary-report
        - Month Summary Report: /reports/month-summary-report
        - Product Sales Report: /reports/product-sales-report
        - Item-wise Sales Report: /reports/item-wise-report
        - Raw Material Reorder Report: /reports/rm-reorder-report
        - Addon Sales Report: /reports/addon-sales-report
        - Outlet Stock Transfer Report: /reports/outlet-stock-report
        - Storage Location Stock Report: /reports/storage-location-stock-report
        - Out of Stock / Product Stock Report: /reports/out-of-stock-product-report
        - Stock Consumption Report: /reports/stock-consumption-report
        - Outlet Inventory Report: /reports/outlet-inventory-report
        - Online Sales Report: /reports/online-sales-report
        - Raw Material Stock Report: /reports/rm-stock-report
        - Central Stock Transactions: /transaction/central-stock-report
        - Outlet Stock Transactions: /transaction/outlet-stock-report
        - Add Purchase Entry: /purchase/add-purchase
        - Local Procurement: /purchase/local-procurement
        - Purchase List: /purchase/purchase-list
        - Purchase Units: /purchase/purchase-units
        - Outlet Stock Transfer Entry: /purchase/outlet-stock-transfer
        - Customer Support Management: /customer-support-mgt
        - Order Cancellation Requests: /cancelation-request
        - Franchise Enquiry Management: /franchise-enquiry
        - Advertisement Management: /content-management/advertisement-mgt
        - Popup Advertisements: /content-management/popup-advertisement
        - Promote Products: /content-management/promote-products
        - Notification Scheduler: /notifications/notification-scheduler
        - Cart Scheduler: /notifications/cart-scheduler
        - Broadcast Notifications: /notifications/broadcast
        - Offer of the Day: /coupons/offer-of-the-day
        - Auto Coupon Allocation: /coupons/auto-coupon-allocation
        - Create Coupon: /coupons/create-coupon
        - Coupons List: /coupons/coupons-list
        - User Coupon Allocation: /coupons/user-coupon-allocation
        - Version Control Settings: /system-preference/version-control
        - System Settings: /system-preference/settings
        - System Logs: /system-preference/logs
        - User Level Rewards: /user-level/view-level-reward
        - Raw Materials Management: /material-mgt/raw-materials
        - Asset Stock Transfer: /asset-mgt/asset-stock-transfer
        - Subscription Management: /subscription/view-subscription
        - Order Summary Report: /summaries/order-summary
        - Item-wise Summary Report: /summaries/item-wise-summary
        - Addon Sales Summary Report: /summaries/addon-sales-summary
        - Coupon Usage Summary Report: /summaries/coupon-usage-summary
        - User Management: /admin-user-mgt/user-mgt
        - Roles and Permissions: /admin-user-mgt/roles
        
        APP CONTEXT:
        ${context || "Xpanse Retail Admin Panel."}`;

        let contents = [
            { role: "user", parts: [{ text: prompt }] }
        ];

        try {
            for (let i = 0; i < 5; i++) {
                const response = await axiosInstance.post(
                    `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`,
                    // `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-pro-preview:generateContent?key=${apiKey}`,
                    {
                        system_instruction: { parts: [{ text: systemPrompt }] },
                        contents: contents,
                        tools: aiService.tools,
                        tool_config: {
                            function_calling_config: {
                                mode: "AUTO"
                            }
                        }
                    }
                );

                const candidate = response.data?.candidates?.[0];
                const modelContent = candidate?.content;

                if (!modelContent) throw new Error("Empty AI response");

                // Update contents for the next potential turn
                contents.push(modelContent);

                const parts = modelContent.parts || [];
                const toolCalls = parts.filter(p => p.function_call || p.functionCall);

                if (toolCalls.length === 0) {
                    const textResponse = parts.map(p => p.text || "").join("").trim();
                    return textResponse || "I'm sorry, I don't have an answer for that right now. Could you try rephrasing?";
                }

                // Execute tools
                const toolParts = await Promise.all(toolCalls.map(async (tc) => {
                    const call = tc.function_call || tc.functionCall;
                    const result = await aiService.executeTool(call.name, call.args);
                    return {
                        function_response: {
                            name: call.name,
                            response: { content: result }
                        }
                    };
                }));

                // Push function responses back to history
                contents.push({
                    role: "function",
                    parts: toolParts
                });
            }

            return "I've processed too many requests. Could you please specify what you need more clearly?";
        } catch (error) {
            console.error("Gemini Assistant Error:", error.response?.data || error.message);
            throw new Error("I had trouble fetching data. Please check your connection or try again later.");
        }
    }
};

export default aiService;
